import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Search, Grid3X3, LayoutList, ChevronLeft, ChevronRight, Car, SlidersHorizontal, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import VehicleCard from "../components/VehicleCard";
import VehicleListItem from "../components/VehicleListItem";
import FilterSidebar from "../components/FilterSidebar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CatalogPage() {
  const [vehicles, setVehicles] = useState([]);
  const [filters, setFilters] = useState({
    brands: [],
    models: [],
    fuel_types: [],
    colors: [],
    price_range: { min: 0, max: 100000 },
    mileage_range: { min: 0, max: 200000 }
  });
  const [selectedFilters, setSelectedFilters] = useState({
    brand: null,
    model: null,
    fuel_type: null,
    color: null,
    price_min: null,
    price_max: null,
    mileage_min: null,
    mileage_max: null
  });
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 12,
    total: 0,
    total_pages: 0
  });
  const [sortBy, setSortBy] = useState("price_asc");
  const [loading, setLoading] = useState(true);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "list"

  useEffect(() => {
    loadFilters();
  }, []);

  useEffect(() => {
    loadVehicles();
  }, [selectedFilters, pagination.page, sortBy]);

  const loadFilters = async () => {
    try {
      const response = await axios.get(`${API}/filters`);
      setFilters(response.data);
    } catch (error) {
      console.error("Errore caricamento filtri:", error);
    }
  };

  const loadVehicles = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('page_size', pagination.page_size);
      params.append('sort_by', sortBy);
      
      Object.entries(selectedFilters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.append(key, value);
        }
      });

      const response = await axios.get(`${API}/vehicles?${params.toString()}`);
      setVehicles(response.data.vehicles || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        total_pages: response.data.total_pages
      }));
    } catch (error) {
      console.error("Errore caricamento veicoli:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = useCallback((newFilters) => {
    setSelectedFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  const handleClearFilters = () => {
    setSelectedFilters({
      brand: null,
      model: null,
      fuel_type: null,
      color: null,
      price_min: null,
      price_max: null,
      mileage_min: null,
      mileage_max: null
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="catalog-page">
      {/* Header */}
      <div className="bg-neutral-950 py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-blue-500 font-semibold uppercase tracking-widest text-sm mb-2">
            Esplora
          </p>
          <h1 
            className="text-4xl md:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            CATALOGO VEICOLI
          </h1>
          <p className="text-zinc-400 max-w-xl">
            {pagination.total} veicoli disponibili nella nostra selezione
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="flex gap-8">
          {/* Sidebar - Desktop */}
          <aside className="hidden lg:block w-72 flex-shrink-0">
            <div className="sticky top-24">
              <FilterSidebar
                filters={filters}
                selectedFilters={selectedFilters}
                onFilterChange={handleFilterChange}
                onClear={handleClearFilters}
              />
            </div>
          </aside>

          {/* Main Content Area */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="bg-white border border-zinc-200 p-4 mb-6 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                {/* Mobile Filter Toggle */}
                <Button
                  variant="outline"
                  className="lg:hidden"
                  onClick={() => setShowMobileFilters(true)}
                  data-testid="mobile-filter-btn"
                >
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  Filtri
                </Button>

                <p className="text-sm text-zinc-600">
                  <span className="font-semibold text-zinc-900">{pagination.total}</span> risultati
                </p>
              </div>

              <div className="flex items-center gap-3">
                {/* View Mode Toggle */}
                <div className="hidden sm:flex items-center border border-zinc-200 rounded">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setViewMode("grid")}
                    className={`rounded-none ${viewMode === "grid" ? "bg-zinc-100" : ""}`}
                    data-testid="view-grid-btn"
                  >
                    <Grid3X3 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setViewMode("list")}
                    className={`rounded-none ${viewMode === "list" ? "bg-zinc-100" : ""}`}
                    data-testid="view-list-btn"
                  >
                    <LayoutList className="w-4 h-4" />
                  </Button>
                </div>

                <Select value={sortBy} onValueChange={setSortBy} data-testid="sort-select">
                  <SelectTrigger className="w-48 h-10 bg-white">
                    <SelectValue placeholder="Ordina per" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="price_asc">Prezzo: crescente</SelectItem>
                    <SelectItem value="price_desc">Prezzo: decrescente</SelectItem>
                    <SelectItem value="mileage_asc">KM: crescente</SelectItem>
                    <SelectItem value="mileage_desc">KM: decrescente</SelectItem>
                    <SelectItem value="year_desc">Anno: più recente</SelectItem>
                    <SelectItem value="year_asc">Anno: meno recente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Vehicle Grid/List */}
            {loading ? (
              <div className={viewMode === "grid" ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" : "flex flex-col gap-4"}>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className={`bg-white animate-pulse ${viewMode === "grid" ? "aspect-[4/5]" : "h-48"}`} />
                ))}
              </div>
            ) : vehicles.length > 0 ? (
              <>
                {viewMode === "grid" ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="vehicle-grid">
                    {vehicles.map((vehicle) => (
                      <VehicleCard key={vehicle.id} vehicle={vehicle} />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col gap-4" data-testid="vehicle-list">
                    {vehicles.map((vehicle) => (
                      <VehicleListItem key={vehicle.id} vehicle={vehicle} />
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {pagination.total_pages > 1 && (
                  <div className="mt-8 flex items-center justify-center gap-2" data-testid="pagination">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page <= 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                        let pageNum;
                        if (pagination.total_pages <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.page <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.page >= pagination.total_pages - 2) {
                          pageNum = pagination.total_pages - 4 + i;
                        } else {
                          pageNum = pagination.page - 2 + i;
                        }
                        
                        return (
                          <Button
                            key={pageNum}
                            variant={pagination.page === pageNum ? "default" : "outline"}
                            size="icon"
                            onClick={() => handlePageChange(pageNum)}
                            className={pagination.page === pageNum ? "bg-blue-600 text-white" : ""}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page >= pagination.total_pages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-16 bg-white border border-zinc-200">
                <Car className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
                <h3 
                  className="text-2xl font-bold text-zinc-700 mb-2"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  Nessun Veicolo Trovato
                </h3>
                <p className="text-zinc-500 mb-6">
                  Prova a modificare i filtri di ricerca
                </p>
                <Button 
                  onClick={handleClearFilters}
                  className="bg-blue-600 text-white hover:bg-blue-700"
                >
                  Resetta i Filtri
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filter Modal */}
      {showMobileFilters && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowMobileFilters(false)} />
          <div className="absolute right-0 top-0 h-full w-full max-w-sm bg-white overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-zinc-200 p-4 flex items-center justify-between">
              <h3 
                className="text-lg font-bold"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                FILTRI
              </h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowMobileFilters(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-4">
              <FilterSidebar
                filters={filters}
                selectedFilters={selectedFilters}
                onFilterChange={(newFilters) => {
                  handleFilterChange(newFilters);
                }}
                onClear={() => {
                  handleClearFilters();
                  setShowMobileFilters(false);
                }}
              />
            </div>
            <div className="sticky bottom-0 bg-white border-t border-zinc-200 p-4">
              <Button
                className="w-full bg-blue-600 text-white hover:bg-blue-700"
                onClick={() => setShowMobileFilters(false)}
              >
                Mostra {pagination.total} risultati
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
